import { useRef, useState } from "react";
import { UploadCloud, FileSpreadsheet, X } from "lucide-react";

export default function FileDropzone({ id, label, helperText, file, onFile, accept = ".xlsx" }) {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = (files) => {
    if (files && files[0]) onFile(files[0]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const clearFile = (e) => {
    e.stopPropagation();
    onFile(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div data-testid={`dropzone-wrapper-${id}`}>
      <div className="flex items-baseline justify-between mb-2">
        <label htmlFor={id} className="label-mono">
          {label}
        </label>
        <span className="font-brand-mono text-[10px] text-[#96a09a] uppercase tracking-[1.5px]">.xlsx</span>
      </div>

      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
        }}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={[
          "dropzone px-5 py-6 sm:py-8 cursor-pointer flex items-center gap-4",
          isDragging && "is-dragging",
          file && "has-file",
        ]
          .filter(Boolean)
          .join(" ")}
        data-testid={`dropzone-${id}`}
      >
        {file ? (
          <>
            <FileSpreadsheet className="w-8 h-8 text-[#007d62] shrink-0" strokeWidth={1.5} />
            <div className="flex-1 min-w-0">
              <div className="font-brand-mono text-sm font-medium text-[#0d0d0d] truncate" data-testid={`file-name-${id}`}>
                {file.name}
              </div>
              <div className="font-brand-mono text-[11px] text-[#565b57] mt-0.5 uppercase tracking-wide">
                {(file.size / 1024).toFixed(1)} KB · Ready
              </div>
            </div>
            <button
              type="button"
              onClick={clearFile}
              className="text-[#565b57] hover:text-[#d6453d] p-1 transition-colors"
              aria-label="Remove file"
              data-testid={`remove-file-${id}`}
            >
              <X className="w-4 h-4" />
            </button>
          </>
        ) : (
          <>
            <UploadCloud className="w-8 h-8 text-[#0d0d0d] shrink-0" strokeWidth={1.5} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[#0d0d0d]">
                Drop .xlsx here or <span className="text-[#007d62] underline underline-offset-2 font-semibold">browse</span>
              </div>
              {helperText && (
                <div className="text-xs text-[#565b57] mt-1 leading-relaxed">{helperText}</div>
              )}
            </div>
          </>
        )}
      </div>

      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`file-input-${id}`}
      />
    </div>
  );
}
